import { useMemo, useState } from "react";
import { ChevronRight } from "lucide-react";
import type { PricebookItem } from "@clevis/shared-types";
import { Card, ConfirmButton, Select, cn } from "@clevis/shared-ui";
import { BulkBar, ListFooter, SearchBox, SortHeader, useListView } from "./ListControls";

// The pricebook as a working list: search, sort, filter by category / kind /
// what's missing (piece rate, cost, SKU, description), and fix many at once
// (piece rate, category, archive). Clicking a row opens it for editing.

export type PricebookRow = PricebookItem & { id: string };
const money = (c: number | null | undefined) => (c === null || c === undefined ? "—" : `$${(c / 100).toFixed(2)}`);

export interface PricebookBulk {
  setPieceRate: (ids: string[], cents: number | null) => Promise<void>;
  setCategory: (ids: string[], category: string) => Promise<void>;
  setArchived: (ids: string[], archived: boolean) => Promise<void>;
}

function BulkField({ placeholder, type = "text", onApply }: { placeholder: string; type?: string; onApply: (v: string) => void }) {
  const [v, setV] = useState("");
  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        onApply(v);
        setV("");
      }}
    >
      <input
        className="w-28 rounded-md border border-white/30 bg-white/10 px-2 py-1 text-xs text-white placeholder:text-white/60 focus:border-white focus:outline-none"
        type={type}
        step={type === "number" ? "0.01" : undefined}
        min={type === "number" ? "0" : undefined}
        placeholder={placeholder}
        value={v}
        onChange={(e) => setV(e.target.value)}
      />
      <button type="submit" className="rounded-md bg-white/15 px-2 py-1 text-xs hover:bg-white/25">
        Set
      </button>
    </form>
  );
}

export function PricebookList({
  items,
  onEdit,
  bulk,
  say,
}: {
  items: PricebookRow[];
  onEdit: (item: PricebookRow) => void;
  bulk: PricebookBulk;
  say: (m: string) => void;
}) {
  const [status, setStatus] = useState<"active" | "archived">("active");
  const [category, setCategory] = useState("");
  const [kind, setKind] = useState("all");
  const [missing, setMissing] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const pool = useMemo(() => items.filter((i) => (status === "archived" ? !!i.archivedAt : !i.archivedAt)), [items, status]);
  const categories = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of pool) m.set(i.category.trim() || "", (m.get(i.category.trim() || "") ?? 0) + 1);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
  }, [pool]);

  const view = useListView(pool, {
    text: (i) => [i.name, i.sku, i.category, i.description],
    sorts: {
      name: (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }),
      category: (a, b) => (a.category || "~").localeCompare(b.category || "~", undefined, { numeric: true }) || a.name.localeCompare(b.name, undefined, { numeric: true }),
      price: (a, b) => a.priceCents - b.priceCents,
      cost: (a, b) => (a.costCents ?? -1) - (b.costCents ?? -1),
      piece: (a, b) => (a.pieceRateCents ?? -1) - (b.pieceRateCents ?? -1),
    },
    defaultSort: "name",
    filter: (i) =>
      (category === "" || (category === "(none)" ? !i.category.trim() : i.category.trim() === category)) &&
      (kind === "all" || (kind === "kit" ? i.kind === "kit" : i.kind !== "kit")) &&
      (missing === "" ||
        (missing === "piece" && !i.pieceRateCents) ||
        (missing === "cost" && i.costCents === null) ||
        (missing === "sku" && !(i.sku ?? "").trim()) ||
        (missing === "description" && !i.description.trim())),
  });
  const allPicked = view.page.length > 0 && view.page.every((i) => picked.has(i.id));
  const done = async (label: string, fn: () => Promise<void>) => {
    const n = picked.size;
    try {
      await fn();
      say(`${label} on ${n} item${n === 1 ? "" : "s"}.`);
      setPicked(new Set());
    } catch (err) {
      say((err as Error).message);
    }
  };
  const cols = "sm:grid-cols-[24px_minmax(0,2.6fr)_minmax(0,1.1fr)_minmax(0,0.9fr)_80px_80px_80px_16px]";
  const missingCount = (k: string) =>
    pool.filter((i) => (k === "piece" ? !i.pieceRateCents : k === "cost" ? i.costCents === null : k === "sku" ? !(i.sku ?? "").trim() : !i.description.trim())).length;

  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center gap-2 border-b border-steel/10 p-3">
        <SearchBox className="min-w-56 flex-1" value={view.query} onChange={view.setQuery} placeholder="Search name, SKU, category, description…" />
        <Select className="w-auto py-1.5 text-sm" aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(([c, n]) => (
            <option key={c || "(none)"} value={c || "(none)"}>
              {c || "Uncategorized"} ({n})
            </option>
          ))}
        </Select>
        <Select className="w-auto py-1.5 text-sm" aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="all">Items and kits</option>
          <option value="item">Items only</option>
          <option value="kit">Kits only</option>
        </Select>
        <Select className="w-auto py-1.5 text-sm" aria-label="Missing" value={missing} onChange={(e) => setMissing(e.target.value)}>
          <option value="">Missing: any</option>
          <option value="piece">Missing piece rate ({missingCount("piece")})</option>
          <option value="cost">Missing cost ({missingCount("cost")})</option>
          <option value="sku">Missing SKU ({missingCount("sku")})</option>
          <option value="description">Missing description ({missingCount("description")})</option>
        </Select>
        <Select
          className="w-auto py-1.5 text-sm"
          aria-label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as "active" | "archived");
            setPicked(new Set());
          }}
        >
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </Select>
      </div>

      <BulkBar count={picked.size} onClear={() => setPicked(new Set())}>
        <span className="text-xs text-white/70">Piece rate $</span>
        <BulkField
          placeholder="25.00"
          type="number"
          onApply={(v) => void done("Piece rate set", () => bulk.setPieceRate([...picked], v.trim() === "" ? null : Math.round(parseFloat(v) * 100)))}
        />
        <span className="text-xs text-white/70">Category</span>
        <BulkField placeholder="Openers" onApply={(v) => void done("Category set", () => bulk.setCategory([...picked], v.trim()))} />
        <span className="[&>span>span:first-child]:text-white">
          <ConfirmButton
            variant="secondary"
            className="border border-white/30 px-2.5 py-1 text-xs"
            confirm={status === "archived" ? `Restore ${picked.size}?` : `Archive ${picked.size}?`}
            confirmLabel={status === "archived" ? "Restore" : "Archive"}
            onConfirm={() => void done(status === "archived" ? "Restored" : "Archived", () => bulk.setArchived([...picked], status !== "archived"))}
          >
            {status === "archived" ? "Restore" : "Archive"}
          </ConfirmButton>
        </span>
      </BulkBar>

      <div className={cn("hidden items-center gap-3 border-b border-steel/10 px-4 py-2 sm:grid", cols)}>
        <input
          type="checkbox"
          aria-label="Select this page"
          checked={allPicked}
          onChange={() =>
            setPicked((p) => {
              const n = new Set(p);
              for (const i of view.page) {
                if (allPicked) n.delete(i.id);
                else n.add(i.id);
              }
              return n;
            })
          }
        />
        <SortHeader label="Name" k="name" view={view} />
        <SortHeader label="Category" k="category" view={view} />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-steel">SKU</span>
        <SortHeader label="Price" k="price" view={view} className="justify-end" />
        <SortHeader label="Cost" k="cost" view={view} className="justify-end" />
        <SortHeader label="Piece" k="piece" view={view} className="justify-end" />
        <span />
      </div>

      {view.page.map((i) => (
        <div
          key={i.id}
          className={cn(
            "grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0 hover:bg-cream/60 sm:gap-3",
            cols,
            picked.has(i.id) && "bg-brandorange/5",
          )}
          onClick={() => onEdit(i)}
        >
          <input
            type="checkbox"
            aria-label={`Select ${i.name}`}
            checked={picked.has(i.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() =>
              setPicked((p) => {
                const n = new Set(p);
                if (n.has(i.id)) n.delete(i.id);
                else n.add(i.id);
                return n;
              })
            }
          />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5">
              {i.photoUrl ? <img src={i.photoUrl} alt="" className="h-6 w-6 shrink-0 rounded object-cover" loading="lazy" /> : null}
              <span className="truncate font-medium text-ink">{i.name}</span>
              {i.kind === "kit" ? (
                <span className="shrink-0 rounded-full bg-brandorange/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-brandorange-dark">Kit · {(i.components ?? []).length}</span>
              ) : null}
            </span>
            {i.description ? <span className="block truncate text-[11px] text-steel">{i.description}</span> : null}
            <span className="block truncate text-[11px] text-steel sm:hidden">
              {[i.category, money(i.priceCents), i.pieceRateCents ? `piece ${money(i.pieceRateCents)}` : ""].filter(Boolean).join(" · ")}
            </span>
          </span>
          <span className="hidden truncate text-steel sm:block">{i.category || "—"}</span>
          <span className="hidden truncate font-mono text-xs text-steel sm:block">{i.sku || "—"}</span>
          <span className="hidden text-right font-semibold text-ink sm:block">{money(i.priceCents)}</span>
          <span className="hidden text-right text-steel sm:block">{money(i.costCents)}</span>
          <span className={cn("hidden text-right sm:block", i.pieceRateCents ? "text-ink" : "text-steel")}>{i.pieceRateCents ? money(i.pieceRateCents) : "—"}</span>
          <ChevronRight size={14} className="text-steel" />
        </div>
      ))}
      {view.filtered.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-steel">{view.total === 0 ? (status === "archived" ? "Nothing archived." : "No items yet — flat-rate pricing starts here.") : "Nothing matches."}</div>
      ) : null}
      <ListFooter shown={view.page.length} matching={view.filtered.length} total={view.total} onMore={view.more} onAll={view.all} noun="items" />
    </Card>
  );
}
